export interface AuraConfig {
  minCoherence: number;
  minPurity: number;
  requireAll: boolean;
}

export interface AuraVerdict {
  coherence: number;
  purityAvg: number;
  purityEach: number[];
  validated: boolean;
  details: { pairs: number };
}

export interface AuraSignal {
  name: string;
  data: number[];
  timestamp: number;
}

export class AuraValidator {
  private static phaseCoherence(a: number[], b: number[]): number {
    // Simplified coherence calculation for web
    if (a.length !== b.length) return 0;
    
    const meanA = a.reduce((sum, val) => sum + val, 0) / a.length;
    const meanB = b.reduce((sum, val) => sum + val, 0) / b.length;
    
    const normalizedA = a.map(val => val - meanA);
    const normalizedB = b.map(val => val - meanB);
    
    let correlation = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < normalizedA.length; i++) {
      correlation += normalizedA[i] * normalizedB[i];
      normA += normalizedA[i] * normalizedA[i];
      normB += normalizedB[i] * normalizedB[i];
    }
    
    const denominator = Math.sqrt(normA * normB) + 1e-12;
    return Math.max(0, Math.min(1, Math.abs(correlation / denominator)));
  }

  private static normalizedEntropy(x: number[], bins: number = 64): number {
    // Calculate histogram-based entropy
    const min = Math.min(...x);
    const max = Math.max(...x);
    const range = max - min;
    
    if (range === 0) return 1.0;
    
    const hist = new Array(bins).fill(0);
    
    for (const val of x) {
      const binIndex = Math.min(bins - 1, Math.floor(((val - min) / range) * bins));
      hist[binIndex]++;
    }
    
    const total = x.length;
    let entropy = 0;
    
    for (const count of hist) {
      if (count > 0) {
        const p = count / total;
        entropy -= p * Math.log2(p);
      }
    }
    
    const maxEntropy = Math.log2(bins);
    return Math.max(0, Math.min(1, 1.0 - entropy / maxEntropy));
  }

  static validateAura(signals: number[][], config: AuraConfig): AuraVerdict {
    const n = signals.length;
    
    if (n < 2) {
      const purity = n === 1 ? [this.normalizedEntropy(signals[0])] : [];
      const validated = purity.length > 0 && purity[0] >= config.minPurity;
      
      return {
        coherence: 0.0,
        purityAvg: purity[0] || 0.0,
        purityEach: purity,
        validated,
        details: { pairs: 0 }
      };
    }

    // Calculate pairwise coherence
    const cohValues: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        cohValues.push(this.phaseCoherence(signals[i], signals[j]));
      }
    }
    
    const coherence = cohValues.length > 0 ? 
      cohValues.reduce((sum, val) => sum + val, 0) / cohValues.length : 0.0;

    // Calculate per-signal purity
    const purities = signals.map(signal => this.normalizedEntropy(signal));
    const purityAvg = purities.reduce((sum, val) => sum + val, 0) / purities.length;

    // Decision logic
    const passCoherence = coherence >= config.minCoherence;
    const passPurity = config.requireAll ? 
      Math.min(...purities) >= config.minPurity :
      purityAvg >= config.minPurity;
    
    const validated = passCoherence && passPurity;

    return {
      coherence,
      purityAvg,
      purityEach: purities,
      validated,
      details: { pairs: cohValues.length }
    };
  }

  static synthesizeSignals(length: number = 2048, sampleRate: number = 200): AuraSignal[] {
    const t = Array.from({ length }, (_, i) => i / sampleRate);
    
    // Heart-like rhythm (1.1 Hz with HRV)
    const heart = t.map(time => {
      const baseFreq = 1.1 + 0.05 * Math.sin(2 * Math.PI * 0.2 * time);
      return Math.sin(2 * Math.PI * baseFreq * time) + 0.05 * (Math.random() - 0.5) * 2;
    });

    // Gaze velocity with saccades
    const gaze = t.map(time => {
      const smooth = 0.7 * Math.sin(2 * Math.PI * 0.6 * time) + 
                    0.3 * Math.sin(2 * Math.PI * 1.2 * time + 0.7);
      const saccade = Math.random() < 0.01 ? 2.0 : 0;
      return smooth + saccade;
    });

    // Intent envelope
    const intent = t.map(time => {
      const envelope = 0.8 * Math.sin(2 * Math.PI * 0.1 * time) + 
                      0.2 * Math.cos(2 * Math.PI * 0.05 * time);
      return Math.max(-1, Math.min(1, envelope));
    });

    return [
      { name: 'Heart Rhythm', data: heart, timestamp: Date.now() },
      { name: 'Gaze Velocity', data: gaze, timestamp: Date.now() },
      { name: 'Intent Envelope', data: intent, timestamp: Date.now() }
    ];
  }
}